import { useState, useEffect } from "react"
import { useSelector, useDispatch, shallowEqual } from "react-redux"
import { DateTime } from "luxon"
import Button from "react-bootstrap/Button"
import { FiAlertTriangle } from "react-icons/fi"

import "./index.scss"
import useT from "utils/hooks/useT"
import { Popup } from "components"
import { Container, Title, Text, Select } from "components/Form"
import { canSign, SIGNATURE_REASONS } from "state_management/lims/segregationOfDuties"
import { fetch as fetchCommentTemplates, selectByContext } from "state_management/lims/limsCommentLibrary"

function reasonLabelKey(reason) {
  return "limsEsigReason" + reason.charAt(0).toUpperCase() + reason.slice(1)
}

// Reusable Part 11 electronic-signature dialog: re-authenticate (password) + state a
// meaning (reason). Enforces segregation of duties (capability + 4-eyes) when given those
// props — as a fail-fast UX gate; the BACKEND re-verifies the password, capability and
// 4-eyes on every signing endpoint and 403s on any failed gate (SPEC-021 B1).
//
// Password handling (SPEC-021 B1): the password is held in component memory only — never
// in redux and never in localStorage. It is passed to `onSign` inside the payload so the
// calling thunk can place it at the TOP LEVEL of the signing request body (the backend
// verifies + discards it); the field is cleared after a successful sign and on unmount.
//
// `onSign` may return a promise: the dialog then shows a busy state and, on rejection,
// surfaces the error (e.g. the backend's 403 "Password verification failed" /
// capability / 4-eyes message) in an inline error panel and stays open so the signer can
// correct the password. The CALLER closes the dialog on success (by unmounting it).
// `requireReason` (PRD-006 K13, default off — non-invasive): the QA-correction flows (amend
// decision / lift hold / reopen OOS) demand a mandatory justification. When set, the reason field
// is required and the dialog refuses to sign with a blank reason — a fail-fast mirror of the server,
// which is authoritative (422 on a blank reason). All existing callers leave it off and are unaffected.
// `commentContext` (F1, SPEC-033, default null — non-invasive): when set to a signature context
// (reviewer / supervisor / qa-release / return-for-correction / oos-conclusion), a "Standard
// comment" dropdown is rendered ABOVE the reason textarea, listing the team's canned comments for
// that context; picking one PRE-FILLS the reason (still freely editable). With no prop the dropdown
// is absent and the dialog is byte-for-byte the old one — fully back-compatible.
export default function ESignatureModal({
  title,
  reasons = SIGNATURE_REASONS,
  requiredCapability = null,
  excludedMemberIds = [],
  requireReason = false,
  commentContext = null,
  onSign,
  onCancel,
}) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const memberId = useSelector((state) => state.auth.memberId)
  const initials = useSelector((state) => (state.members[memberId] && state.members[memberId].initials) || "")
  const gate = useSelector(
    (state) => canSign(state, { capability: requiredCapability, excludedMemberIds }),
    shallowEqual
  )
  // Canned standard comments for this context (empty unless `commentContext` is set).
  const commentTemplates = useSelector(
    (state) => (commentContext ? selectByContext(state, commentContext) : []),
    shallowEqual
  )

  // Lazily load the comment-library when a context is requested. The modal renders in several
  // places (analysis task, Sample View, LIMS screens), not all of which pre-fetch the slice, so
  // it self-fetches — cheap, team-scoped, and a no-op when `commentContext` is unset. An empty
  // result just means no dropdown (back-compatible).
  useEffect(() => {
    if (commentContext) dispatch(fetchCommentTemplates())
  }, [dispatch, commentContext])

  // The dropdown is the signed ACTION/meaning (approved/reviewed/released/authorized);
  // the free-text is the REASON (justification) recorded on the audit entry.
  const actionOptions = reasons.map((r) => ({ id: r, name: t(reasonLabelKey(r)) }))
  const [action, setAction] = useState(reasons[0] || "")
  const [reason, setReason] = useState("")
  const [password, setPassword] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // K13: with requireReason on, a blank reason blocks the sign (fail-fast; the server 422s too).
  const reasonMissing = requireReason && !reason.trim()

  const handleSign = (e) => {
    if (e) e.preventDefault()
    if (!password || busy) return
    if (reasonMissing) {
      setError(t("limsEsigReasonRequired"))
      return
    }
    setError(null)
    const result = onSign({
      memberId,
      initials,
      action,
      reason: reason || null,
      timestamp: DateTime.now().toISO(),
      // In memory only — the consuming thunk lifts it onto the request body top level
      // and never persists it (redux/localStorage) nor embeds it in stored payloads.
      password,
    })
    if (result && typeof result.then === "function") {
      setBusy(true)
      result
        .then(() => setPassword(""))
        .catch((err) => setError((err && err.message) || t("limsEsigFailed")))
        .finally(() => setBusy(false))
    } else {
      setPassword("")
    }
  }

  return (
    <Popup nonScrollable title={title || t("limsEsigTitle")} onCancel={onCancel}>
      {!gate.allowed ? (
        <div className="LimsEsig-blocked">
          <div className="LimsEsig-blocked-panel">
            <FiAlertTriangle className="LimsEsig-blocked-icon" />
            <div className="LimsEsig-blocked-body">
              <p className="LimsEsig-blocked-title">{t("limsEsigBlockedTitle")}</p>
              <p className="LimsEsig-blocked-reason">{t(gate.reasonKey)}</p>
              <p className="LimsEsig-blocked-detail">{t("limsEsigBlockedDetail")}</p>
            </div>
          </div>
          <div className="LimsEsig-buttons">
            <Button variant="secondary" onClick={onCancel}>
              {t("close")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="LimsEsig">
          {/* RR6: a plain <div>, NOT a <form>. This dialog is frequently rendered INSIDE another
              form (e.g. the analysis-task ConfigContainer form); a nested <form> is invalid HTML
              that made the "Sign" submit bubble into a native full-page reload — closing the whole
              analysis modal. Signing is driven by the button's onClick instead. */}
          <Container>
            <Title>{t("limsAuditAction")}</Title>
            <Select
              isMulti={false}
              collection={actionOptions}
              value={action}
              onChange={(v) => setAction(v[0] || "")}
              menuPlacement="bottom"
            />
          </Container>
          {commentContext && commentTemplates.length > 0 && (
            <Container>
              <Title>{t("limsCommentStandard")}</Title>
              {/* Picking a standard comment PRE-FILLS the reason (still editable). The Select is
                  not bound to a stored value — it is a one-shot picker that writes into `reason`. */}
              <Select
                isMulti={false}
                collection={commentTemplates.map((tpl) => ({ id: tpl.id, name: tpl.text }))}
                value=""
                onChange={(v) => {
                  const picked = commentTemplates.find((tpl) => tpl.id === (v[0] || ""))
                  if (picked) setReason(picked.text)
                }}
                menuPlacement="bottom"
              />
              <p className="LimsEsig-help LimsEsig-comment-help">{t("limsCommentStandardHint")}</p>
            </Container>
          )}
          <Container>
            <Title>{requireReason ? `${t("limsEsigReason")} *` : t("limsEsigReason")}</Title>
            <textarea
              className="LimsEsig-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {requireReason && <p className="LimsEsig-help">{t("limsEsigReasonRequiredHint")}</p>}
          </Container>
          <Container>
            <Title>{t("password")}</Title>
            <Text type="password" required value={password} onChange={setPassword} />
          </Container>
          <p className="LimsEsig-help">{t("limsEsigPasswordHelp")}</p>
          {error && (
            <div className="LimsEsig-blocked-panel">
              <FiAlertTriangle className="LimsEsig-blocked-icon" />
              <div className="LimsEsig-blocked-body">
                <p className="LimsEsig-blocked-title">{t("limsEsigFailed")}</p>
                <p className="LimsEsig-blocked-reason">{error}</p>
              </div>
            </div>
          )}
          <div className="LimsEsig-buttons">
            <Button variant="light" onClick={onCancel} disabled={busy}>
              {t("cancel")}
            </Button>
            <Button type="button" variant="primary" onClick={handleSign} disabled={busy || reasonMissing}>
              {t("limsEsigSign")}
            </Button>
          </div>
        </div>
      )}
    </Popup>
  )
}
